import type { FundDetail } from "@/data/fund-detail-types";
import { useLiveHoldings } from "@/hooks/use-etf-live-holdings";
import {
  EMPTY,
  formatChange,
  formatChangePct,
  formatPrice,
  useFundLiveData,
} from "@/hooks/use-fund-live-data";
import { track } from "@/lib/analytics";
import { renderNow } from "@/lib/build-clock";
import { formatInceptionCanonical } from "@/lib/format-inception";
import { useEffect, useSyncExternalStore } from "react";
import { FundWhyInvest } from "./fund-why-invest";

const BUFFER_SERIES_SUFFIX = new RegExp(
  "\\s+[—–-]\\s+(?:January|February|March|April|May|June|July|August|" +
    "September|October|November|December)\\s+Series\\s*$",
);

function buildBufferTitle(fund: FundDetail): string {
  return fund.fullName.replace(BUFFER_SERIES_SUFFIX, "");
}

/**
 * Render a stored `heroTags` pill. The expense-ratio pill is the one tag that
 * can drift: cloned/copied fund blueprints bake a literal "X% Expense Ratio"
 * string that isn't updated when `expenseRatio` changes, so prod records can
 * show a stale value (e.g. 0.45%) while the stats bar shows the real 0.20%.
 * When a tag is an expense-ratio pill we substitute the live `expenseRatio`
 * so the two always agree; every other tag renders verbatim.
 */
function renderHeroTag(tag: string, expenseRatio: string | undefined): string {
  if (/expense ratio/i.test(tag) && expenseRatio) {
    return tag.replace(/[\d.]+\s*%/, expenseRatio);
  }
  return tag;
}

export function FundHero({ fund }: { fund: FundDetail }) {
  const isLeveraged = fund.type === "leveraged";
  const isBuffer = fund.type === "buffer";
  const isFixed = fund.type === "fixed";
  const liveHoldingsCount = useLiveHoldings(fund).holdingsCount;

  // Funnel step: a meaningful fund-page view (one per ticker render).
  useEffect(() => {
    track("fund_view", { intent: "medium", fundTicker: fund.ticker });
  }, [fund.ticker]);

  return (
    <section
      id="sec-overview"
      data-fund-section="name fullName ticker summary description heroTags siteCategory managementStyle bufferConfig.seriesMonth"
      className="border-b border-[var(--etf-border)] grid grid-cols-1 min-[901px]:grid-cols-2 relative overflow-hidden"
    >
      <div className="relative z-[1] pt-14 px-10 pb-12 max-[900px]:pt-10 max-[900px]:px-6 max-[900px]:pb-8 max-[480px]:pt-8 max-[480px]:px-5 max-[480px]:pb-7 min-[901px]:border-r border-[var(--etf-border)] max-[900px]:border-b">
        <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[var(--etf-orange)] mb-5 font-[family-name:var(--font-instrument)]">
          <span className="inline-block w-6 h-px bg-[var(--etf-orange)]" />
          {fund.categoryLabel}
        </div>

        {isBuffer && fund.seriesMonth && (
          <div
            data-fund-field="bufferConfig.seriesMonth"
            className="inline-block font-[family-name:var(--font-geist)] text-[12px] font-semibold tracking-[0.16em] uppercase text-white bg-[var(--etf-orange)] px-4 py-2 mb-5 self-start"
          >
            {fund.seriesMonth} Series
          </div>
        )}

        <h1
          data-fund-field="fullName name"
          className={`font-[family-name:var(--font-f37bolton)] font-normal leading-none tracking-[-0.02em] text-[var(--etf-white)] mb-6 ${isBuffer ? "text-[clamp(36px,5vw,58px)]" : "text-[clamp(48px,6.5vw,76px)] max-[900px]:text-[clamp(36px,5vw,56px)] max-[480px]:text-[32px]"}`}
        >
          {isBuffer ? buildBufferTitle(fund) : fund.fullName}
        </h1>

        {!isBuffer && (
          <div
            data-fund-field="ticker"
            className="font-[family-name:var(--font-f37bolton)] text-[clamp(48px,6.5vw,76px)] max-[900px]:text-[clamp(36px,5vw,56px)] max-[480px]:text-[32px] font-normal leading-none tracking-[-0.02em] text-[var(--etf-orange)] mb-6"
          >
            {fund.ticker}
          </div>
        )}

        <p
          data-fund-field="summary description"
          className={`text-[15px] max-[480px]:text-[14px] font-light ${isLeveraged ? "" : "italic"} text-[var(--etf-white2)] leading-[1.7] max-w-[480px] max-[900px]:max-w-none mb-9 max-[480px]:mb-6`}
        >
          {fund.heroDesc}
        </p>

        <div data-fund-field="heroTags" className="flex gap-2 flex-wrap">
          {fund.heroTags && fund.heroTags.length > 0 ? (
            fund.heroTags.map((tag, i) => (
              <span
                key={tag}
                className={`font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] border px-3 py-[5px] ${
                  i === 0
                    ? "border-[var(--etf-orange)] text-[var(--etf-orange)]"
                    : "border-[var(--etf-border)] text-[var(--etf-white3)]"
                }`}
              >
                {renderHeroTag(tag, fund.expenseRatio)}
              </span>
            ))
          ) : isLeveraged ? (
            <>
              <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] border border-[var(--etf-orange)] text-[var(--etf-orange)] px-3 py-[5px]">
                2x Daily Exposure
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] border border-[var(--etf-border)] text-[var(--etf-white3)] px-3 py-[5px]">
                Daily Reset
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] border border-[var(--etf-border)] text-[var(--etf-white3)] px-3 py-[5px]">
                {fund.expenseRatio} Expense Ratio
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] border border-[var(--etf-border)] text-[var(--etf-white3)] px-3 py-[5px]">
                Inception {formatInceptionCanonical(fund.inception)}
              </span>
            </>
          ) : (
            <>
              <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] border border-[var(--etf-orange)] text-[var(--etf-orange)] px-3 py-[5px]">
                {fund.expenseRatio} Expense Ratio
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] border border-[var(--etf-border)] text-[var(--etf-white3)] px-3 py-[5px]">
                {fund.exchange}
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] border border-[var(--etf-border)] text-[var(--etf-white3)] px-3 py-[5px]">
                {liveHoldingsCount} {isFixed ? "Bonds" : "Holdings"}
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] border border-[var(--etf-border)] text-[var(--etf-white3)] px-3 py-[5px]">
                Inception {formatInceptionCanonical(fund.inception)}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="relative z-[1] pt-14 px-10 pb-12 max-[900px]:pt-8 max-[900px]:px-6 max-[900px]:pb-10 max-[480px]:pt-6 max-[480px]:px-5 max-[480px]:pb-8 flex flex-col justify-center bg-[var(--etf-black2)]">
        <PriceBlock fund={fund} />
        {isBuffer && fund.bufferConsider && fund.bufferConsider.length > 0 && (
          <BufferConsiderBlock items={fund.bufferConsider} />
        )}
        {!isBuffer && fund.whyInvest.length > 0 && (
          <div className="mt-5">
            <FundWhyInvest items={fund.whyInvest} />
          </div>
        )}
      </div>
    </section>
  );
}

function isMarketOpenAt(now: Date): boolean {
  const et = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const day = et.getDay();
  const mins = et.getHours() * 60 + et.getMinutes();
  return day >= 1 && day <= 5 && mins >= 570 && mins < 960;
}

function isMarketOpenNow(): boolean {
  return isMarketOpenAt(new Date());
}

// No-op subscription: nothing pushes market-state updates between renders;
// the snapshot is simply re-read whenever the component re-renders — the same
// cadence as the old inline isMarketOpenNow() call.
const subscribeNever = () => () => {};

/**
 * Market-hours flag that is safe to read during render. On a normal client
 * mount the first render reads the real clock — behavior identical to calling
 * isMarketOpenNow() inline. Under static rendering the server snapshot reads
 * the BUILD clock (renderNow) — the prerenderer sets __SSG_NOW__ during its
 * pass and embeds the same instant in the page, so the hydration render
 * reproduces the baked state exactly; React then re-checks the client
 * snapshot as hydration completes and re-renders to true visit-time state
 * without a hydration mismatch.
 */
function useIsMarketOpen(): boolean {
  return useSyncExternalStore(subscribeNever, isMarketOpenNow, () => isMarketOpenAt(renderNow()));
}

function formatLiveTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const formatted = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  });
  return formatted.replace(/(\d+:\d+):\d+/, "$1");
}

function formatLiveDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.split("T")[0] ?? "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "America/New_York",
  });
}

function PriceBlock({ fund }: { fund: FundDetail }) {
  const isLeveraged = fund.type === "leveraged";
  const isBuffer = fund.type === "buffer";
  const live = useFundLiveData(fund.ticker, fund) ?? EMPTY;

  const livePrice = live.price != null ? formatPrice(live.price) : null;
  const liveChange = live.change != null ? formatChange(live.change) : null;
  const liveChangePct = live.changePct != null ? formatChangePct(live.changePct) : null;

  const isStaticEmpty = (v: string | null | undefined) =>
    !v ||
    v === "—" ||
    v === "$X" ||
    v === "X" ||
    v.toUpperCase() === "TBD" ||
    v.toUpperCase() === "N/A";
  const headlinePrice = livePrice ?? (isStaticEmpty(fund.price.current) ? "—" : fund.price.current);
  const changeText = liveChange ?? (isStaticEmpty(fund.price.change) ? null : fund.price.change);
  const changePctText =
    liveChangePct ?? (isStaticEmpty(fund.price.changePct) ? null : fund.price.changePct);
  const isNegative = (changeText ?? "").includes("-");
  const marketOpen = useIsMarketOpen();
  const isLive = live.source === "live" && marketOpen;
  const liveTimeLabel = isLive && live.asOfDate ? formatLiveTime(live.asOfDate) : null;
  const eyebrowDate = isBuffer
    ? fund.ticker
    : isLive && live.asOfDate
      ? `${formatLiveDate(live.asOfDate)}, ${liveTimeLabel}`
      : // Prefer the LIVE daily_nav date; fall back to the static config date
        // only pre-ingestion (fund.navAsOf = the stale `aumAsOf` config string).
        (live.asOfDate?.split("T")[0] ?? live.navAsOfDate ?? fund.navAsOf ?? fund.dateLabel);

  return (
    <div>
      <div className="flex items-center gap-2.5 font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.14em] text-[var(--etf-white3)] uppercase mb-2">
        {isBuffer ? (
          marketOpen ? (
            <div className="flex items-center gap-[5px] text-[var(--etf-green)]">
              <span className="w-[5px] h-[5px] bg-[var(--etf-green)] rounded-full animate-[etf-blink_2s_infinite]" />
              Live
            </div>
          ) : (
            <div className="flex items-center gap-[5px] text-[var(--etf-red)]">
              <span className="w-[5px] h-[5px] bg-[var(--etf-red)] rounded-full" />
              Market Closed
            </div>
          )
        ) : (
          isLive && (
            <div className="flex items-center gap-[5px] text-[var(--etf-green)]">
              <span className="w-[5px] h-[5px] bg-[var(--etf-green)] rounded-full animate-[etf-blink_2s_infinite]" />
              Live
            </div>
          )
        )}
        {isLeveraged ? eyebrowDate : <>Market Price &middot; {eyebrowDate}</>}
      </div>

      <div className="font-[family-name:var(--font-f37bolton)] text-[clamp(48px,6.5vw,76px)] text-[var(--etf-white)] tracking-[-0.02em] leading-none mb-7">
        {headlinePrice}
      </div>

      <div className="hidden items-baseline gap-3 mb-7">
        <span
          className={`font-[family-name:var(--font-instrument)] text-[20px] font-medium ${isNegative ? "text-[var(--etf-red)]" : "text-[var(--etf-green)]"}`}
        >
          {changeText}
        </span>
        {changePctText && (
          <span
            className={`font-[family-name:var(--font-instrument)] text-sm opacity-80 ${isNegative ? "text-[var(--etf-red)]" : "text-[var(--etf-green)]"}`}
          >
            ({changePctText})
          </span>
        )}
      </div>

      <a
        href="#sec-brokerages"
        data-analytics-event="buy_cta"
        data-analytics-intent="high"
        data-analytics-fund={fund.ticker}
        className={`block mt-4 w-full max-w-[360px] max-[900px]:max-w-full px-6 py-4 rounded-[2px] bg-[var(--etf-orange)] text-white text-center text-[13px] font-medium tracking-[0.14em] uppercase no-underline cursor-pointer border-none hover:opacity-[0.88] transition-opacity duration-150 shadow-[0_4px_20px_rgba(255,85,0,0.25),0_0_40px_rgba(255,85,0,0.08)] ${isBuffer ? "font-[family-name:var(--font-geist)]" : "font-[family-name:var(--font-instrument)]"}`}
      >
        {isLeveraged ? "Trade" : "Buy"} {fund.ticker} &rarr;
      </a>
    </div>
  );
}

function BufferConsiderBlock({ items }: { items: { title: string; desc: string }[] }) {
  return (
    <div data-fund-section="reasons" className="mt-5 max-md:mt-4">
      <div className="font-[family-name:var(--font-geist)] text-xs leading-[1.5] tracking-[0.15em] uppercase text-[var(--etf-orange)] mb-2.5 pl-4">
        Reasons to Consider
      </div>
      <div className="flex flex-col">
        {items.map((item, i) => (
          <div
            key={item.title}
            className="border-l-2 border-l-[var(--etf-orange)] px-4 max-md:px-3 py-1.5 max-md:py-[5px] flex items-start gap-3 border-b border-b-[var(--etf-border2)] last:border-b-0"
          >
            <span className="font-[family-name:var(--font-geist)] text-xs leading-[1.5] text-[var(--etf-orange)] opacity-50 pt-0.5 shrink-0">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div>
              <div className="text-xs leading-[1.5] font-semibold text-[var(--etf-white)] mb-[3px] tracking-[0.02em]">
                {item.title}
              </div>
              <div className="text-[11px] italic text-[var(--etf-white3)] leading-[1.5] font-light">
                {item.desc}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
